import { useState } from "react";
import { ProjectTile } from "./components/ProjectTile";
import { ProjectTable } from "./components/ProjectTable";
import { Pagination } from "./components/Pagination";
import { SearchIcon, GridViewIcon, ListViewIcon } from "../../components/icons";

// Import local images
import {
  yellowtailFpsoImg,
  jaguarFpsoImg,
  marjanGasOilImg,
  companyLogo1,
  companyLogo2,
  companyLogo3,
  companyLogo4,
  avatarImg,
} from "../../assets/images/projects";
import { ChevronDownIcon } from "../../components/icons/ChevronDownIcon";

// Mock project data
const mockProjects = [
  {
    id: 1,
    thumbnailUrl: yellowtailFpsoImg,
    icon1Url: companyLogo1,
    icon2Url: companyLogo2,
    title: "Yellowtail FPSO",
    badge: "YEL-FP-002",
    type: "Offshore Platform",
    stats: {
      month: 25,
      tags: 121987,
      nextMilestone: "Hull & Turret Mating",
    },
    isFavorite: false,
  },
  {
    id: 2,
    thumbnailUrl: jaguarFpsoImg,
    icon1Url: companyLogo3,
    icon2Url: companyLogo2,
    title: "FPSO Jaguar",
    badge: "JAG-FP-001",
    type: "Offshore platform",
    stats: {
      month: 28,
      tags: 122456,
      nextMilestone: "Topside module integration",
    },
    isFavorite: true,
  },
  {
    id: 3,
    thumbnailUrl: marjanGasOilImg,
    icon1Url: avatarImg,
    icon2Url: companyLogo4,
    title: "Marjan gas & oil separation",
    badge: "MAR-GS-005",
    type: "Offshore platform",
    stats: {
      month: 35,
      tags: 11890,
      nextMilestone: "GOSP-4 commissioning",
    },
    isFavorite: false,
  },
  {
    id: 4,
    thumbnailUrl: yellowtailFpsoImg,
    icon1Url: avatarImg,
    icon2Url: companyLogo4,
    title: "MPF Nassau",
    badge: "CMHI-OR-012",
    type: "Onshore EPC",
    stats: {
      month: 18,
      tags: 231500,
      nextMilestone: "Mechanical completion",
    },
    isFavorite: false,
  },
];

type TabType = "all" | "my" | "request-access";
type ViewType = "grid" | "list";

export function ProjectsPage() {
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("last-updated");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [viewType, setViewType] = useState<ViewType>("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState(mockProjects);

  const handleFavoriteToggle = (projectId: number) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, isFavorite: !p.isFavorite } : p
      )
    );
  };

  const handleProjectClick = (projectId: number) => {
    // Navigate to data-exploration for FPSO Jaguar (id: 2)
    if (projectId === 2) {
      window.location.href = "/data-exploration";
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter((project) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      project.title.toLowerCase().includes(searchLower) ||
      project.badge.toLowerCase().includes(searchLower) ||
      project.type.toLowerCase().includes(searchLower) ||
      project.stats.nextMilestone.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="box-border content-stretch flex flex-col gap-0 items-start pb-0 pt-[16px] px-[32px] relative size-full">
      {/* Page Header */}
      <div className="box-border content-stretch flex flex-col items-start pb-[16px] pt-[24px] px-0 relative shrink-0 w-full">
        {/* Main Content */}
        <div className="box-border content-stretch flex items-start pb-[8px] pt-0 px-0 relative shrink-0 w-full">
          <div className="basis-0 content-stretch flex gap-[10px] grow items-center min-h-px min-w-px relative shrink-0">
            <p
              className="font-['Noto_Sans:SemiCondensed',sans-serif] font-[653] leading-[28px] relative shrink-0 text-[#292a2e] text-[24px] text-nowrap whitespace-pre"
              style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 87.5" }}
            >
              Projects
            </p>
            <div className="border border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex items-center justify-center min-h-[24px] px-[12px] py-0 relative rounded-[3px] shrink-0">
              <div className="content-stretch flex gap-[4px] items-center justify-center relative shrink-0">
                <div
                  className="flex flex-col font-['Noto_Sans:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#292a2e] text-[12px] text-center text-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[16px] whitespace-pre">15 projects</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="box-border content-stretch flex items-center justify-between pb-0 pt-[16px] px-0 relative shrink-0 w-full">
          {/* Search and Filters */}
          <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
            {/* Search */}
            <div className="border border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex gap-[8px] h-[32px] items-center pl-[12px] py-0 relative rounded-[5px] shrink-0 w-[280px]">
              <div className="content-stretch flex items-start relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[16px]">
                  <SearchIcon className="block size-full text-[#6b6e76]" />
                </div>
              </div>
              <input
                type="text"
                placeholder="Search project"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="basis-0 font-['SF_Pro:Regular',sans-serif] font-normal leading-[20px] grow min-h-px min-w-px relative shrink-0 text-[#6b6e76] text-[14px] bg-transparent border-none outline-none"
                style={{ fontVariationSettings: "'wdth' 100" }}
              />
            </div>

            {/* Filter by Type */}
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="content-stretch flex h-[32px] items-center min-h-[32px] relative rounded-[3px] shrink-0 w-[200px]">
                <div className="basis-0 border border-solid border-[rgba(11,18,40,0.14)] content-stretch flex grow h-[32px] items-center min-h-px min-w-px relative rounded-[3px] shrink-0">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="basis-0 font-['SF_Pro:Regular',sans-serif] font-normal leading-[20px] grow min-h-px min-w-px relative shrink-0 text-[#292a2e] text-[14px] bg-transparent border-none outline-none px-3 appearance-none cursor-pointer"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    <option value="all">Filter by type</option>
                    <option value="offshore">Offshore Platform</option>
                    <option value="onshore">Onshore EPC</option>
                    <option value="fpso">FPSO</option>
                  </select>
                  <div className="content-stretch flex items-start relative shrink-0 pr-2 pointer-events-none">
                    <div className="overflow-clip relative shrink-0 size-[24px]">
                      <ChevronDownIcon className="block size-full text-[#292A2E]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sort by */}
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="content-stretch flex h-[32px] items-center min-h-[32px] relative rounded-[3px] shrink-0 w-[200px]">
                <div className="basis-0 border border-solid border-[rgba(11,18,40,0.14)] content-stretch flex grow h-[32px] items-center min-h-px min-w-px relative rounded-[3px] shrink-0">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="basis-0 font-['SF_Pro:Regular',sans-serif] font-normal leading-[20px] grow min-h-px min-w-px relative shrink-0 text-[#292a2e] text-[14px] bg-transparent border-none outline-none px-3 appearance-none cursor-pointer"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    <option value="last-updated">Sort by last updated</option>
                    <option value="name">Sort by name</option>
                    <option value="date-created">Sort by date created</option>
                  </select>
                  <div className="content-stretch flex items-start relative shrink-0 pr-2 pointer-events-none">
                    <div className="overflow-clip relative shrink-0 size-[24px]">
                      <ChevronDownIcon className="block size-full text-[#292A2E]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="box-border content-stretch flex gap-[8px] items-center pl-[32px] pr-0 py-0 relative shrink-0">
            {/* Toggle */}
            <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
              <div
                className="flex flex-col font-['Noto_Sans:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#292a2e] text-[14px] text-center text-nowrap"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">
                  Show favorites only
                </p>
              </div>
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`h-[16px] relative rounded-[8px] shrink-0 w-[32px] transition-colors ${
                  showFavoritesOnly
                    ? "bg-[#1868db]"
                    : "bg-[rgba(11,18,40,0.14)]"
                }`}
              >
                <div
                  className={`absolute size-[12px] top-[2px] rounded-full bg-white transition-all ${
                    showFavoritesOnly ? "right-[2px]" : "left-[2px]"
                  }`}
                />
              </button>

              {/* Grid View */}
              <button
                onClick={() => setViewType("grid")}
                className={`content-stretch flex items-center justify-center relative rounded-[3px] shrink-0 p-1 transition-colors ${
                  viewType === "grid"
                    ? "bg-[rgba(5,21,36,0.06)]"
                    : "hover:bg-[rgba(5,21,36,0.06)]"
                }`}
              >
                <div className="overflow-clip relative shrink-0 size-[24px]">
                  <GridViewIcon className="block size-full text-[#292A2E]" />
                </div>
              </button>

              {/* List View */}
              <button
                onClick={() => setViewType("list")}
                className={`content-stretch flex items-center justify-center relative rounded-[3px] shrink-0 p-1 transition-colors ${
                  viewType === "list"
                    ? "bg-[rgba(5,21,36,0.06)]"
                    : "hover:bg-[rgba(5,21,36,0.06)]"
                }`}
              >
                <div className="overflow-clip relative shrink-0 size-[24px]">
                  <ListViewIcon className="block size-full text-[#292A2E]" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="h-[32px] overflow-clip relative shrink-0 w-full">
        <div className="absolute border border-solid border-[rgba(11,18,40,0.14)] bottom-0 h-[2px] left-0 right-0 rounded-[1px]" />
        <div className="absolute h-[32px] left-0 right-0 top-1/2 translate-y-[-50%]">
          <div className="absolute content-stretch flex h-[32px] items-start left-[-8px] top-0">
            <button
              onClick={() => setActiveTab("all")}
              className={`box-border content-stretch flex flex-col items-center overflow-clip px-[8px] py-[6px] relative shrink-0 cursor-pointer transition-colors hover:bg-[rgba(5,21,36,0.06)] ${
                activeTab === "all" ? "" : ""
              }`}
            >
              <div
                className={`flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[14px] text-center text-nowrap ${
                  activeTab === "all" ? "text-[#292a2e]" : "text-[#6b6e76]"
                }`}
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">All projects</p>
              </div>
              {activeTab === "all" && (
                <div className="absolute border border-solid border-[#1868db] bottom-0 h-[2px] left-[8px] right-[8px] rounded-[2px]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("my")}
              className={`box-border content-stretch flex gap-[4px] items-center overflow-clip px-[8px] py-[6px] relative shrink-0 cursor-pointer transition-colors hover:bg-[rgba(5,21,36,0.06)]`}
            >
              <div
                className={`flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[14px] text-center text-nowrap ${
                  activeTab === "my" ? "text-[#292a2e]" : "text-[#6b6e76]"
                }`}
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">My projects</p>
              </div>
              {activeTab === "my" && (
                <div className="absolute border border-solid border-[#1868db] bottom-0 h-[2px] left-[8px] right-[8px] rounded-[2px]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("request-access")}
              className={`box-border content-stretch flex gap-[4px] items-center overflow-clip px-[8px] py-[6px] relative shrink-0 cursor-pointer transition-colors hover:bg-[rgba(5,21,36,0.06)]`}
            >
              <div
                className={`flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[14px] text-center text-nowrap ${
                  activeTab === "request-access"
                    ? "text-[#292a2e]"
                    : "text-[#6b6e76]"
                }`}
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">Request access</p>
              </div>
              {activeTab === "request-access" && (
                <div className="absolute border border-solid border-[#1868db] bottom-0 h-[2px] left-[8px] right-[8px] rounded-[2px]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content - Grid or List View */}
      <div className="box-border content-stretch flex flex-col gap-[16px] items-start px-0 py-[16px] relative shrink-0 w-full">
        {/* List View (Table) */}
        {viewType === "list" && (
          <ProjectTable
            projects={filteredProjects}
            onFavoriteToggle={handleFavoriteToggle}
            onProjectClick={handleProjectClick}
          />
        )}

        {/* Grid View */}
        {viewType === "grid" && (
          <div className="grid grid-cols-2 gap-[16px] w-full">
            {filteredProjects.map((project) => (
              <ProjectTile
                key={project.id}
                {...project}
                onFavoriteToggle={() => handleFavoriteToggle(project.id)}
                onProjectClick={() => handleProjectClick(project.id)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={10}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
